import React from 'react';
import { MarkElement, MarkElementProps } from '@mui/x-charts/LineChart';
import { BiometricDatapointRecord } from 'services/biometricsService';
import { MEAL_SHAPES, MealReading, isHollowMeal } from 'features/progress/utils/bloodGlucoseMeals';

export const ShapedMarkElement = ({ id, shape, color, ...rest }: MarkElementProps) => (
  <MarkElement
    {...rest}
    id={id}
    color={color}
    shape={MEAL_SHAPES[id as MealReading] ?? shape}
    {...(!isHollowMeal(id as MealReading) && ({ sx: { fill: color } } as object))}
  />
);

export const mgPerDl = (value: number | null) => (value === null ? null : `${value} mg/dL`);

export const CHART_SLOTS = 7;
const EDGE_GAP = 0.75;

export const formatDayLabel = (dateKey: string) =>
  new Date(`${dateKey}T00:00:00`).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });

const getDayPositions = (count: number) =>
  Array.from({ length: count }, (_, index) => EDGE_GAP + (CHART_SLOTS - count) / 2 + index);

// Only a full window gets the anchor: the line starts at the y-axis.
export const getChartXAxis = (days: string[], formatLabel = formatDayLabel) => {
  const positions = getDayPositions(days.length);
  const hasAnchor = days.length >= CHART_SLOTS;
  const axisMax = CHART_SLOTS - 1 + 2 * EDGE_GAP;
  return {
    hasAnchor,
    xAxis: {
      id: 'days',
      scaleType: 'linear' as const,
      min: 0,
      max: axisMax,
      data: hasAnchor ? [0, ...positions] : positions,
      tickInterval: [...positions, axisMax],
      valueFormatter: (position: number) => {
        const index = positions.indexOf(position);
        return index === -1 ? '' : formatLabel(days[index]);
      },
      tickSize: 0,
    },
  };
};

// A single reading is just a mark, so no anchor line.
export const withAnchorPoint = (
  values: Array<number | null>,
  hasAnchor: boolean,
): Array<number | null> => {
  if (!hasAnchor) return values;
  const readingCount = values.filter((value) => value !== null).length;
  return [readingCount > 1 ? (values[0] ?? null) : null, ...values];
};

export const isAnchorIndex = (index: number, hasAnchor: boolean) => hasAnchor && index === 0;

const Y_AXIS_STEP = 60;
const DEFAULT_Y_AXIS_MAX = 300;

export const EMPTY_Y_AXIS = { min: 0, max: 250, step: 50 };

export const getYAxisRange = (
  datapoints: BiometricDatapointRecord[],
): { min: number; max: number } => {
  const highest = Math.max(0, ...datapoints.map((point) => point.value));
  return {
    min: 0,
    max: Math.max(DEFAULT_Y_AXIS_MAX, Math.ceil(highest / Y_AXIS_STEP) * Y_AXIS_STEP),
  };
};

export const getYAxisTicks = (min: number, max: number, step = Y_AXIS_STEP): number[] =>
  Array.from({ length: (max - min) / step + 1 }, (_, index) => min + index * step);
